import { ADVANCED_MODEL_STEPS } from 'app/modules/detectionModels/constants';
import {
  DetectionModelMetadata,
  DetectionModelTemplate,
  EditingAdvancedDetectionModel,
  ModelValidationPayload,
} from 'app/modules/detectionModels/models';
import { IRSchema } from 'app/modules/rulesAdvanced/models';
import { LocalStorageKeys } from 'app/shared/constants/localStorage';
import { getLocalStorageJSON } from 'app/shared/utils/localStorage';
import { formatDistanceToNow } from 'date-fns';

export interface AdvancedCreateSnapshot {
  currentStepIndex: number;
  irRule: IRSchema;
  irTemplate: DetectionModelTemplate | null;
  metaData: DetectionModelMetadata;
  ruleWriterSessionId?: string;
  trmSuppressAlerts: boolean;
  validationPayload: ModelValidationPayload;
}

export interface RuleCreationDraft {
  currentStep: number;
  editingAdvancedDetectionModel: EditingAdvancedDetectionModel;
  ruleWriterSessionId?: string;
  savedAt: string;
}

function buildEditingModelFromSnapshot(
  snapshot: AdvancedCreateSnapshot,
  existing: EditingAdvancedDetectionModel,
): EditingAdvancedDetectionModel {
  const step = ADVANCED_MODEL_STEPS[snapshot.currentStepIndex];

  return {
    ...existing,
    irSchema: snapshot.irRule,
    metadata: snapshot.metaData,
    validationPayload: snapshot.validationPayload,
    template: snapshot.irTemplate,
    trm_suppress_alerts: snapshot.trmSuppressAlerts,
    initialStep: step?.id,
  };
}

function hasLocalProgress(snapshot: AdvancedCreateSnapshot): boolean {
  if (snapshot.currentStepIndex > 0) {
    return true;
  }
  if (snapshot.irTemplate) {
    return true;
  }
  if (snapshot.irRule.facts.length) {
    return true;
  }
  if ((snapshot.irRule.rule_condition?.operands?.length ?? 0) > 0) {
    return true;
  }
  if (snapshot.metaData.title?.trim()) {
    return true;
  }
  if (snapshot.metaData.description?.trim()) {
    return true;
  }
  if (snapshot.ruleWriterSessionId) {
    return true;
  }
  return false;
}

export function saveRuleCreationDraft(draft: RuleCreationDraft): void {
  try {
    localStorage.setItem(
      LocalStorageKeys.DMB_RULE_CREATION_DRAFT,
      JSON.stringify(draft),
    );
  } catch {
    // ignore quota/private-mode failures
  }
}

export function getRuleCreationDraft(): RuleCreationDraft | null {
  const stored = getLocalStorageJSON(LocalStorageKeys.DMB_RULE_CREATION_DRAFT);
  if (!stored || typeof stored !== 'object') {
    return null;
  }
  const draft = stored as RuleCreationDraft;
  if (
    typeof draft.currentStep !== 'number' ||
    typeof draft.savedAt !== 'string' ||
    !draft.editingAdvancedDetectionModel
  ) {
    return null;
  }
  return draft;
}

export function saveAdvancedCreateDraft(
  snapshot: AdvancedCreateSnapshot,
  editingDetectionModel: EditingAdvancedDetectionModel,
): void {
  if (!hasLocalProgress(snapshot)) {
    clearRuleCreationDraft();
    return;
  }

  saveRuleCreationDraft({
    currentStep: snapshot.currentStepIndex,
    editingAdvancedDetectionModel: buildEditingModelFromSnapshot(
      snapshot,
      editingDetectionModel,
    ),
    ruleWriterSessionId: snapshot.ruleWriterSessionId,
    savedAt: new Date().toISOString(),
  });
}

export function clearRuleCreationDraft(): void {
  try {
    localStorage.removeItem(LocalStorageKeys.DMB_RULE_CREATION_DRAFT);
  } catch {
    // ignore quota/private-mode failures
  }
}

export function getRuleCreationDraftStepLabel(currentStep: number): string {
  return ADVANCED_MODEL_STEPS[currentStep]?.label ?? 'rule creation';
}

export function getRuleCreationDraftSavedDescription(savedAt: string): string {
  const savedDate = new Date(savedAt);
  if (Number.isNaN(savedDate.getTime())) {
    return 'Saved recently.';
  }

  return `Saved ${formatDistanceToNow(savedDate, { addSuffix: true })}.`;
}
